import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Flex, Space, theme } from 'antd';
import { PlayCircleFilled, PauseCircleFilled } from '@ant-design/icons';

const AudioMessage = ({ url, duration, sender = 'customer' }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [totalDuration, setTotalDuration] = useState(typeof duration === 'number' ? duration : 0);
    const [numBars, setNumBars] = useState(40); // Default number of bars
    const waveformRef = useRef(null);
    const audioRef = useRef(null);

    const { token } = theme.useToken();

    // Throttled resize handler to prevent excessive calculations during orientation changes
    const calculateBars = useCallback(() => {
        if (waveformRef.current) {
            // Calculate number of bars based on container width
            // Assuming we want each bar + gap to take up ~6px (4px bar + 2px gap)
            const containerWidth = waveformRef.current.offsetWidth;
            const newNumBars = Math.floor(containerWidth / 6);
            setNumBars(Math.max(20, Math.min(newNumBars, 100))); // Keep bars between 20 and 100
        }
    }, []);

    // Use ResizeObserver instead of window resize for better performance
    useEffect(() => {
        if (!waveformRef.current) return;

        // Calculate initially
        calculateBars();

        // Use ResizeObserver for more efficient resize detection
        const resizeObserver = new ResizeObserver((entries) => {
            // Throttle using requestAnimationFrame to avoid blocking
            requestAnimationFrame(() => {
                calculateBars();
            });
        });

        resizeObserver.observe(waveformRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [calculateBars]);

    useEffect(() => {
        if (typeof duration === 'number' && duration > 0) {
            setTotalDuration(duration);
            setIsLoading(false);
        }
    }, [duration]);

    const handlePlayPause = (event) => {
        event.stopPropagation();
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const total = audioRef.current?.duration || totalDuration || 0;
            const percentage = total ? (audioRef.current.currentTime / total) * 100 : 0;
            setProgress(percentage);
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Generate visualization bars based on dynamic numBars
    const waveform = Array.from({ length: numBars }, () => Math.random() * 60 + 40);

    const containerStyle = {
        padding: '8px 8px 4px 8px',
        borderRadius: 0,
        backgroundColor: sender !== 'customer' ? token.colorPrimary : token.colorBgContainer,
    };

    const waveformContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        height: 32,
        width: '100%',
        flexGrow: 1,
    };

    const barStyle = (index) => ({
        width: '100%',
        height: `${waveform[index]}%`,
        backgroundColor: sender !== 'customer'
            ? (progress / 100) * waveform.length > index
                ? token.colorWhite
                : 'rgba(255, 255, 255, 0.5)'
            : (progress / 100) * waveform.length > index
                ? token.colorPrimary
                : token.colorBorder,
        transition: 'all 0.2s',
        borderRadius: token.borderRadiusXS,
        minWidth: '2px',
        flexGrow: 1,
        maxWidth: '4px',
    });

    const timeStyle = {
        fontSize: token.fontSizeSM,
        color: sender !== 'customer' ? token.colorWhite : token.colorTextSecondary,
        minWidth: 80,
    };

    return (
        <div style={containerStyle}>
            <Flex gap={2} align="center">
                <Button
                    type={sender !== 'customer' ? 'text' : 'default'}
                    icon={isPlaying ? (
                        <PauseCircleFilled style={{
                            fontSize: 24,
                            color: sender !== 'customer' ? token.colorWhite : token.colorPrimary
                        }} />
                    ) : (
                        <PlayCircleFilled style={{
                            fontSize: 24,
                            color: sender !== 'customer' ? token.colorWhite : token.colorPrimary
                        }} />
                    )}
                    onClick={handlePlayPause}
                    loading={isLoading}
                    style={{
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                    }}
                />

                <div style={waveformContainerStyle} ref={waveformRef}>
                    {waveform.map((_, index) => (
                        <div
                            key={index}
                            style={barStyle(index)}
                        />
                    ))}
                </div>

                <span style={timeStyle}>
                    {formatTime(currentTime)} / {formatTime(totalDuration)}
                </span>

                <audio
                    ref={audioRef}
                    src={url}
                    onLoadedMetadata={(e) => {
                        setIsLoading(false);
                        const d = e.currentTarget.duration || 0;
                        if (!totalDuration && d) {
                            setTotalDuration(d);
                        }
                    }}
                    onCanPlay={(e) => {
                        setIsLoading(false);
                        const d = e.currentTarget.duration || 0;
                        if (!totalDuration && d) {
                            setTotalDuration(d);
                        }
                    }}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                    style={{ display: 'none' }}
                />
            </Flex>
        </div>
    );
};

export default AudioMessage;